import type { ReactNode } from 'react';
import { useState } from 'react';
import { faArrowDownToLine } from '@awesome.me/kit-4a6bf60416/icons/classic/regular';

import {
  AWS_WAF_SECURITY_CHECK_MESSAGE,
  isAwsWafInterruption,
  isAwsWafResponseError,
} from '@/components/shared/aws-waf/aws-waf-response';
import { ensureAwsWafToken } from '@/components/shared/aws-waf/aws-waf-token';
import { useAwsWafSdk } from '@/components/shared/aws-waf/use-aws-waf-sdk';
import { FormFieldInput } from '@/components/shared/form/form-field-inputs';
import type { FormField } from '@/components/shared/form/form-fields';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { MutedText, Paragraph } from '@/components/ui/typography';
import { useIsPageEditor } from '@/hooks/use-is-page-editor';
import { useIsPreviewMode } from '@/hooks/use-is-preview-mode';
import { useSiteButtons } from '@/hooks/use-site-buttons';
import http from '@/lib/http';
import { publicFormSubmissionsPath } from '@/lib/routes';
import { getDocument } from '@/lib/utils';

type SubmitState = 'idle' | 'submitting' | 'success' | 'error';
type FieldValue = string | string[];

interface DownloadableFileResponse {
  fileName?: string | null;
  url?: string | null;
}

interface SubmissionResponse {
  downloadableFile?: DownloadableFileResponse | null;
}

function blankSubmittedValue(value: FieldValue | undefined): boolean {
  if (Array.isArray(value)) {
    return value.length === 0;
  }

  return !value?.trim();
}

function requiredFieldErrors(fields: FormField[], values: Record<string, FieldValue>): string[] {
  return fields
    .filter((field) => field.required && blankSubmittedValue(values[field.key]))
    .map((field) => `${field.label} is required`);
}

interface FormSettings {
  submitBehavior?: {
    type?: string;
    message?: string | null;
    url?: string | null;
  };
  downloadableFile?: {
    enabled?: boolean;
    fileName?: string | null;
    url?: string | null;
  };
  submitButton?: {
    text?: string | null;
    type?: string | null;
  };
}

export function Form({
  formId,
  fields,
  submitLabel,
  settings,
}: {
  formId: string;
  fields: FormField[];
  submitLabel?: string;
  settings?: FormSettings;
}) {
  const isPageEditor = useIsPageEditor();
  const isPreview = useIsPreviewMode();
  const { shape, buttonStyle } = useSiteButtons();
  const submissionsDisabled = isPageEditor || isPreview;

  const awsWafIntegrationScriptUrl = useAwsWafSdk(!submissionsDisabled);

  const sorted = [...fields].sort((a, b) => a.position - b.position);
  const [values, setValues] = useState<Record<string, FieldValue>>(
    Object.fromEntries(sorted.map((f) => [f.key, f.type === 'multiple_choice' ? [] : ''])),
  );
  const [submitState, setSubmitState] = useState<SubmitState>('idle');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [submittedDownloadableFile, setSubmittedDownloadableFile] =
    useState<DownloadableFileResponse | null>(null);

  const submitBehavior = settings?.submitBehavior;
  const effectiveSubmitLabel = submitLabel ?? settings?.submitButton?.text ?? 'Submit';
  const submitButtonColorScheme =
    settings?.submitButton?.type === 'secondary' ? 'secondary' : 'primary';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submissionsDisabled) return;

    setSubmitState('submitting');
    setErrorMessage('');
    setSubmittedDownloadableFile(null);

    const validationErrors = requiredFieldErrors(sorted, values);
    if (validationErrors.length > 0) {
      setErrorMessage(validationErrors.join(', '));
      setSubmitState('error');
      return;
    }

    try {
      await ensureAwsWafToken(awsWafIntegrationScriptUrl);
      const response = await http.post<SubmissionResponse>(publicFormSubmissionsPath(formId), {
        submission: values,
      });

      if (isAwsWafInterruption(response.status, response.headers)) {
        setErrorMessage(AWS_WAF_SECURITY_CHECK_MESSAGE);
        setSubmitState('error');
        return;
      }

      if (!submissionsDisabled && submitBehavior?.type === 'redirect' && submitBehavior.url) {
        getDocument()?.location.assign(submitBehavior.url);
        return;
      }

      setSubmittedDownloadableFile(response.data?.downloadableFile ?? null);
      setSubmitState('success');
      setValues(
        Object.fromEntries(sorted.map((f) => [f.key, f.type === 'multiple_choice' ? [] : ''])),
      );
    } catch (err: unknown) {
      if (isAwsWafResponseError(err)) {
        setErrorMessage(AWS_WAF_SECURITY_CHECK_MESSAGE);
      } else {
        const response =
          err && typeof err === 'object' && 'response' in err
            ? (err.response as { data?: { errors?: string[] } })
            : null;
        setErrorMessage(
          Array.isArray(response?.data?.errors)
            ? response.data.errors.join(', ')
            : 'Unable to send your message. Please try again.',
        );
      }
      setSubmitState('error');
    }
  };

  const confirmationMessage =
    submitBehavior?.message?.trim() || "Thank you! We'll be in touch soon.";
  const hasDownload = Boolean(submittedDownloadableFile?.url);

  if (submitState === 'success') {
    return (
      <div className="flex w-full flex-col items-center justify-center gap-3 rounded-md bg-background px-6 py-5 text-center text-foreground shadow-sm">
        <Paragraph className="font-semibold text-foreground">{confirmationMessage}</Paragraph>
        {hasDownload && (
          <Button variant="outline" size="sm" shape={shape} asChild>
            <a
              href={submittedDownloadableFile?.url ?? ''}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Icon icon={faArrowDownToLine} />
              {submittedDownloadableFile?.fileName || 'Download file'}
            </a>
          </Button>
        )}
        <Button
          variant="outline"
          size="sm"
          shape={shape}
          onClick={() => {
            setSubmittedDownloadableFile(null);
            setSubmitState('idle');
          }}
        >
          Send another message
        </Button>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-4"
      inert={submissionsDisabled ? true : undefined}
    >
      {sorted.map((field) => (
        <FormFieldInput
          key={field.key}
          id={`${formId}-${field.key}`}
          field={field}
          value={values[field.key] ?? (field.type === 'multiple_choice' ? [] : '')}
          disabled={false}
          onChange={(val) => setValues((prev) => ({ ...prev, [field.key]: val }))}
        />
      ))}

      {submitState === 'error' && (
        <MutedText className="text-sm text-destructive">{errorMessage}</MutedText>
      )}

      <Button
        type="submit"
        colorScheme={submitButtonColorScheme}
        className="w-full"
        shape={shape}
        buttonStyle={buttonStyle}
        disabled={submitState === 'submitting'}
      >
        {submitState === 'submitting' ? 'Sending…' : effectiveSubmitLabel}
      </Button>
    </form>
  );
}

export function FormRenderer({
  form,
  submitLabel,
  fallback = <MutedText className="text-sm">Form not available.</MutedText>,
}: {
  form?: { id?: string; fields?: FormField[]; settings?: FormSettings };
  submitLabel?: string;
  fallback?: ReactNode;
}) {
  const { id, fields, settings } = form ?? {};
  const hasForm = !!id && !!fields?.length;

  if (!hasForm) {
    return <>{fallback}</>;
  }

  return <Form formId={id} fields={fields} submitLabel={submitLabel} settings={settings} />;
}
